import { Holiday, LeaveRequest, LeaveType } from "../models/leaveModels";
import {
  MAX_ATTACHMENT_MB,
  NoticeRule,
  medCertRequiredFromDays,
  noticeRuleFor,
} from "../config/leavePolicy";
import { addDays, addMonths, formatLongDate, todayISO } from "./dateUtils";
import { addWorkingDays, isWeekend, isWorkingDay, nonWorkingHolidayDates } from "./workingDays";

/** Requests that hold their dates: pending or approved. */
export function isActiveRequest(r: LeaveRequest): boolean {
  return r.status === "Pending" || r.status === "Approved" || r.status === "Auto-Approved";
}

export interface DateWindow {
  rule: NoticeRule;
  /** Earliest pickable date — in the past for types that allow backdating. */
  earliest: string;
}

/** Which dates a request of `days` days of `leaveType` may use, filed today. */
export function dateWindowFor(
  leaveType: LeaveType,
  days: number,
  holidays: Holiday[],
  today: string = todayISO(),
): DateWindow {
  const rule = noticeRuleFor(leaveType.code, days, leaveType.leadTimeDays);
  const { notice } = rule;
  let earliest =
    rule.backdateDays > 0
      ? addWorkingDays(today, -rule.backdateDays, holidays)
      : notice.unit === "months"
        ? addMonths(today, notice.amount)
        : addWorkingDays(today, notice.amount, holidays);
  // A month's notice can land on a weekend/holiday; start from the first day
  // that can actually be picked so the hint never names an unpickable date.
  const holidayDates = nonWorkingHolidayDates(holidays);
  while (!isWorkingDay(earliest, holidayDates)) earliest = addDays(earliest, 1);
  return { rule, earliest };
}

/** Dates already covered by the employee's pending/approved requests -> label. */
export function bookedDates(requests: LeaveRequest[]): Map<string, string> {
  const booked = new Map<string, string>();
  for (const r of requests.filter(isActiveRequest)) {
    for (const d of r.leaveDates) booked.set(d, `${r.leaveTypeCode} · ${r.status}`);
  }
  return booked;
}

/**
 * Returns a checker giving the reason a date can't be picked, or null when it
 * can. Shared by the date picker (tooltips) and validation.
 */
export function makeDateChecker(
  dateWindow: DateWindow,
  holidays: Holiday[],
  booked: Map<string, string>,
): (iso: string) => string | null {
  const holidayNames = new Map(
    holidays
      .filter((h) => h.type !== "Special Working")
      .map((h) => [h.date.slice(0, 10), h.name] as const),
  );
  return (iso) => {
    if (isWeekend(iso)) return "Weekend";
    const holiday = holidayNames.get(iso);
    if (holiday) return `Holiday: ${holiday}`;
    if (iso < dateWindow.earliest) {
      return dateWindow.rule.backdateDays > 0
        ? "More than 2 weeks ago"
        : `Too soon. ${dateWindow.rule.summary}`;
    }
    const filed = booked.get(iso);
    if (filed) return `Already filed (${filed})`;
    return null;
  };
}

export interface LeaveSplit {
  /** Balance left once pending requests are counted; undefined when not tracked. */
  available?: number;
  /** Paid days already claimed by this employee's pending requests of this type. */
  heldByPending: number;
  paidDays: number;
  /** Days beyond the available balance, filed as Leave Without Pay. */
  lwopDays: number;
}

/**
 * Split a request into paid days and LWOP. Pending requests hold their paid
 * days against the balance so two pending requests can't spend the same days.
 * Types that don't deduct (or with no balance record) are fully paid.
 */
export function computeLeaveSplit(
  days: number,
  leaveType: LeaveType | undefined,
  remaining: number | undefined,
  existingRequests: LeaveRequest[],
  year: number,
): LeaveSplit {
  if (!leaveType?.deductsBalance || remaining === undefined) {
    return { heldByPending: 0, paidDays: days, lwopDays: 0 };
  }
  const heldByPending = existingRequests
    .filter(
      (r) =>
        r.status === "Pending" &&
        r.leaveTypeCode === leaveType.code &&
        r.startDate.startsWith(String(year)),
    )
    .reduce((sum, r) => sum + Math.max(0, r.workingDays - r.lwopDays), 0);
  const available = Math.max(0, remaining - heldByPending);
  const paidDays = Math.min(days, available);
  return { available, heldByPending, paidDays, lwopDays: days - paidDays };
}

export interface ValidationInput {
  leaveType?: LeaveType;
  /** Number of days requested (0 = not entered yet). */
  days: number;
  /** Selected leave dates, sorted YYYY-MM-DD. */
  dates: string[];
  reason: string;
  /** The file picked in the form, if any. */
  attachment?: { name: string; size: number };
  onProbation: boolean;
  /** False when the type deducts but there's no balance row for this year. */
  hasBalanceRecord: boolean;
  split: LeaveSplit;
  /** From makeDateChecker; present once days + type are chosen. */
  checkDate?: (iso: string) => string | null;
}

export interface ValidationResult {
  /** Policy violations — block submit. */
  errors: string[];
  /** Missing input — blocks submit; shown as "still needed". */
  todo: string[];
  /** Worth knowing, but the request can be filed. */
  warnings: string[];
  ok: boolean;
}

/**
 * Validate a leave request before submit: completeness, probation eligibility,
 * notice/backdating window, weekends/holidays, overlap, balance/LWOP, med cert.
 */
export function validateLeaveRequest(input: ValidationInput): ValidationResult {
  const errors: string[] = [];
  const todo: string[] = [];
  const warnings: string[] = [];
  const {
    leaveType,
    days,
    dates,
    reason,
    attachment,
    onProbation,
    hasBalanceRecord,
    split,
    checkDate,
  } = input;

  if (days <= 0) todo.push("Select the number of days");
  if (!leaveType) todo.push("Select a leave type");
  if (days > 0 && leaveType && dates.length < days) {
    const left = days - dates.length;
    todo.push(`Plot ${left} more date${left === 1 ? "" : "s"} on the calendar`);
  }
  if (!reason.trim()) todo.push("Add a reason");

  if (leaveType && onProbation && !leaveType.availableDuringProbation) {
    errors.push(`${leaveType.title} is not available during probation.`);
  }

  if (days > 0 && dates.length > days) {
    errors.push(
      `You picked ${dates.length} dates for a ${days}-day request. Unselect ${dates.length - days}.`,
    );
  }

  if (checkDate) {
    for (const d of dates) {
      const problem = checkDate(d);
      if (problem) errors.push(`${formatLongDate(d)} can't be used: ${problem}`);
    }
  }


  if (leaveType?.deductsBalance && days > 0) {
    if (!hasBalanceRecord) {
      warnings.push(
        `No ${leaveType.code} balance record found for this year. Ask HR to set it up.`,
      );
    } else if (split.lwopDays > 0) {
      const pending =
        split.heldByPending > 0
          ? ` (${split.heldByPending} already held by pending requests)`
          : "";
      warnings.push(
        split.paidDays === 0
          ? `You have no ${leaveType.code} balance left${pending}, so all ${days} day(s) will be Leave Without Pay.`
          : `Only ${split.paidDays} day(s) of ${leaveType.code} are available${pending}. The last ${split.lwopDays} day(s) will be Leave Without Pay.`,
      );
    } else if (split.available !== undefined && split.available - days <= 1) {
      warnings.push(
        `This will leave you with ${split.available - days} day(s) of ${leaveType.code}.`,
      );
    }
  }

  const medCertFrom = leaveType ? medCertRequiredFromDays(leaveType) : undefined;
  if (medCertFrom !== undefined && days >= medCertFrom && !attachment) {
    todo.push("Attach your medical certificate");
  } else if (leaveType?.requiresDocuments && !attachment) {
    warnings.push(
      `${leaveType.title} usually requires supporting documents. Attach one if you have it.`,
    );
  }

  if (attachment && attachment.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
    errors.push(
      `${attachment.name} is ${(attachment.size / 1024 / 1024).toFixed(1)} MB. Attachments can be up to ${MAX_ATTACHMENT_MB} MB. Try a PDF or a smaller photo.`,
    );
  }

  return { errors, todo, warnings, ok: errors.length === 0 && todo.length === 0 };
}
